import React from 'react';
import {
  Box,
  Container,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  Heading,
  Text,
  useToast,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useAppData } from '../../store/dataStore';
import { useForm } from '../../hooks/useForm';
import PhoneOTPInput from './PhoneOTPInput';

// Toggle OTP verification on/off — set to true to re-enable
const ENABLE_OTP_VERIFICATION = false;

const MotionBox = motion(Box);

// -------------------- THANK YOU PAGE COMPONENT --------------------
const WebinarThankYou = () => {
  return (
    <Box
      py={{ base: 24, md: 32 }}
      textAlign="center"
      bg="white"
      color="brand.700"
      px={6}
    >
      <MotionBox
        maxW="2xl"
        mx="auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Heading
          as="h2"
          fontSize={{ base: '3xl', md: '4xl' }}
          fontWeight="bold"
          mb={6}
          color="brand.700"
        >
          🎉 You’re Registered Successfully!
        </Heading>

        <Text fontSize="lg" mb={8} color="brand.600">
          Check your email for webinar details and joining link.
          <br />
          You’ll also receive exclusive learning materials to kickstart your AI
          career.
        </Text>

        <VStack spacing={4}>
          <Button
            as="a"
            href="/beginner-track"
            size="lg"
            colorScheme="green"
            variant="solid"
            w={{ base: 'full', md: 'auto' }}
          >
            Explore Beginner Track
          </Button>

          <Button
            as="a"
            href="/experienced-track"
            size="lg"
            variant="outline"
            colorScheme="green"
            w={{ base: 'full', md: 'auto' }}
          >
            Explore Experienced Track
          </Button>

          <Button
            as="a"
            href="/"
            size="md"
            variant="link"
            color="brand.600"
            mt={2}
          >
            Back to Home
          </Button>
        </VStack>

        <Box mt={12}>
          <Text fontSize="sm" color="gray.500">
            Didn’t receive an email yet? Check your spam folder or reach us at{' '}
            <a
              href="mailto:support@ojasamirai.com"
              style={{ color: '#2b6cb0' }}
            >
              support@ojasamirai.com
            </a>
          </Text>
        </Box>
      </MotionBox>
    </Box>
  );
};

// -------------------- MAIN COMPONENT --------------------
const WebinarRegistration = ({ data }) => {
  const { appData } = useAppData();
  const { nextWebinarDate, nextWebinarTime } = appData;
  const toast = useToast();

  // Track submission success
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  // Track phone verification
  const [phoneVerified, setPhoneVerified] = React.useState(false);
  const [verifiedPhone, setVerifiedPhone] = React.useState('');
  const [phoneValidationFailed, setPhoneValidationFailed] = React.useState(false);

  // Enhanced safety check with nested defaults
  const componentData = {
    title: data?.title || 'Reserve Your Spot Now',
    subtitle: data?.subtitle || 'Join thousands learning to build AI careers',
    formLabels: {
      name: data?.formLabels?.name || 'Full Name',
      email: data?.formLabels?.email || 'Email Address',
      phone: data?.formLabels?.phone || 'Phone (webinar link will be sent on whatsapp)',
    },
    placeholders: {
      name: data?.placeholders?.name || 'Your name',
      email: data?.placeholders?.email || 'your@email.com',
      phone: data?.placeholders?.phone || '+91-XXXXXXXXXX',
    },
    buttonText: data?.buttonText || 'Reserve My Spot',
    footerText: data?.footerText || '100% Free • No Credit Card Required',
  };

  // Initialize form state using the custom hook
  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    webinar: componentData.title,
    courseId: '1',
    webinarDate: nextWebinarDate,
    webinarTime: nextWebinarTime,
  };

  const { formData, handleChange, handleSubmit, isSubmitting, prefilledData } =
    useForm(initialFormData, 'webinar_registration');

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Check if phone is verified or validation failed (allowing bypass)
    // Skipped when ENABLE_OTP_VERIFICATION is false
    if (ENABLE_OTP_VERIFICATION && !phoneVerified && !phoneValidationFailed) {
      toast({
        title: 'Phone Verification Required',
        description: 'Please verify your phone number to continue.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      // Add verified phone to form data
      const formDataWithPhone = {
        ...formData,
        phone: ENABLE_OTP_VERIFICATION ? verifiedPhone : formData.phone,
        phoneVerified: ENABLE_OTP_VERIFICATION ? phoneVerified : false,
        phoneValidationFailed: ENABLE_OTP_VERIFICATION ? phoneValidationFailed : false,
      };

      // Submit the enhanced form data
      const submitEvent = { preventDefault: () => {} };
      const originalFormData = formData;

      // Temporarily update formData to include phone verification
      Object.assign(formData, formDataWithPhone);

      await handleSubmit(submitEvent);

      // Restore original formData
      Object.assign(formData, originalFormData);

      toast({
        title: 'Registration successful!',
        description: 'Check your email for access details.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      console.log('GA Event: webinar_watch');

      // ✅ Show Thank You Page
      setIsSubmitted(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to register. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // ✅ Conditionally render Thank You page
  if (isSubmitted) {
    return <WebinarThankYou />;
  }

  return (
    <Box py={{ base: 16, md: 24 }} bg="brand.700" color="white">
      <Container maxW="container.md">
        <MotionBox
          textAlign="center"
          mb={10}
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Heading
            as="h2"
            fontSize={{ base: '2xl', md: '4xl' }}
            fontFamily="heading"
            mb={4}
          >
            {componentData.title}
          </Heading>
          <Text fontSize={{ base: 'md', md: 'lg' }}>
            {componentData.subtitle}
          </Text>
        </MotionBox>

        <MotionBox
          as="form"
          onSubmit={handleFormSubmit}
          bg="white"
          p={{ base: 6, md: 10 }}
          borderRadius="2xl"
          boxShadow="2xl"
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <VStack spacing={6} align="stretch">
            {/* Show prefilled data alert */}
            {prefilledData && (prefilledData.name || prefilledData.email) && (
              <Alert
                status="info"
                borderRadius="md"
                fontSize="sm"
                color="brand.600"
              >
                <AlertIcon />
                We've prefilled your information from previous visits
              </Alert>
            )}

            <FormControl isRequired>
              <FormLabel color="brand.700" fontWeight="600">
                {componentData.formLabels.name}
              </FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={componentData.placeholders.name}
                focusBorderColor="accent.green"
                size="lg"
                color="brand.800"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="brand.700" fontWeight="600">
                {componentData.formLabels.email}
              </FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={componentData.placeholders.email}
                focusBorderColor="accent.green"
                size="lg"
                color="brand.800"
              />
            </FormControl>

            {/* Phone — OTP when enabled, plain input when disabled */}
            {ENABLE_OTP_VERIFICATION ? (
              <>
                <PhoneOTPInput
                  isRequired={true}
                  onVerified={(phone) => {
                    setPhoneVerified(true);
                    setVerifiedPhone(phone);
                    setPhoneValidationFailed(false);
                  }}
                  onSendFailed={(phone) => {
                    setPhoneValidationFailed(true);
                    setVerifiedPhone(phone);
                    setPhoneVerified(false);
                  }}
                />

                {phoneValidationFailed && !phoneVerified && (
                  <Alert status="info" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    Unable to verify your phone number at this time. You may
                    continue with your registration.
                  </Alert>
                )}
              </>
            ) : (
              <FormControl>
                <FormLabel color="brand.700" fontWeight="600">
                  {componentData.formLabels.phone}
                </FormLabel>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={componentData.placeholders.phone}
                  focusBorderColor="accent.green"
                  size="lg"
                  color="brand.800"
                />
              </FormControl>
            )}

            {/* Hidden webinar field */}
            <input type="hidden" name="webinar" value={componentData.title} />

            {/* Hidden recaptcha container for Firebase Auth */}
            <div id="recaptcha-container"></div>

            <Button
              type="submit"
              size="lg"
              bg="accent.green"
              color="white"
              _hover={{ bg: 'accent.gold', color: 'brand.800' }}
              width="full"
              py={7}
              fontSize="xl"
              fontWeight="700"
              isLoading={isSubmitting}
              loadingText="Registering..."
              isDisabled={ENABLE_OTP_VERIFICATION && !phoneVerified && !phoneValidationFailed}
            >
              {componentData.buttonText}
            </Button>

            <Text fontSize="sm" color="brand.500" textAlign="center">
              {componentData.footerText}
            </Text>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default WebinarRegistration;
